import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Home, KeyRound, Loader2 } from "lucide-react";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import api from "@/lib/api";
import { toast } from "sonner";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/cgjm5igj_FB%20Cover%20logo.png";

export default function DashboardLayout({ subtitle, navItems, active, onNav, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);

  const sendReset = async () => {
    if (!user?.email) return;
    setResetting(true);
    try {
      await api.post("/auth/forgot-password", { email: user.email, origin_url: window.location.origin });
      toast.success(`Password reset link sent to ${user.email}`);
    } catch {
      toast.error("Could not send reset link. Please try again.");
    } finally { setResetting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-navy text-white sticky top-0 h-screen">
        <div className="px-4 py-6 flex flex-col items-center gap-2 border-b border-white/10">
          <img src={LOGO} alt="My Best Body Club" className="h-16 w-auto object-contain rounded-lg bg-white p-1" />
          <div className="text-center">
            <div className="font-heading font-semibold leading-tight">My Best Body Club</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50">{subtitle}</div>
          </div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => onNav(item.id)} data-testid={`nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${active === item.id ? "bg-teal text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
              <item.icon className="w-4 h-4" /> {item.label}
            </button>
          ))}
        </nav>
        <div className="shrink-0 px-3 py-4 border-t border-white/10 space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10">
            <Home className="w-4 h-4" /> Home
          </button>
          <button onClick={sendReset} disabled={resetting} data-testid="dash-reset-password"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10 disabled:opacity-50">
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Reset password
          </button>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="dash-logout"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile top nav */}
        <div className="lg:hidden sticky top-0 z-30 bg-navy text-white px-4 py-3 flex items-center gap-2 overflow-x-auto">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => onNav(item.id)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium ${active === item.id ? "bg-teal" : "bg-white/10"}`}>
              {item.label}
            </button>
          ))}
          <button onClick={sendReset} className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs bg-white/10">Reset password</button>
          <button onClick={() => { logout(); navigate("/"); }} className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs bg-white/10">Logout</button>
        </div>

        <header className="hidden lg:flex items-center justify-between px-8 py-5 border-b border-[#E7E5E4] bg-white">
          <div>
            <h1 className="font-heading text-xl text-navy capitalize">{active}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium text-navy">{user?.name}</div>
              <div className="text-xs text-[#78716C]">{user?.email}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-teal text-white flex items-center justify-center font-heading text-sm">
              {(user?.name || "U").charAt(0)}
            </div>
          </div>
        </header>

        <main className="p-6 lg:p-8 max-w-[1500px] mx-auto">{children}</main>
      </div>
      {user?.role !== "admin" && <FeedbackWidget />}
    </div>
  );
}
